import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { getAptitudeResult } from '../../../services/assessmentService';

const percentToStroke = (value) => {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, value));
  const dashoffset = circumference - (clamped / 100) * circumference;
  return { circumference, dashoffset };
};

export default function ResultPage() {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const preload = async () => {
      const cached = sessionStorage.getItem('aptitude_result_payload');
      if (cached) {
        const parsed = JSON.parse(cached);
        setResult(parsed);
      } else {
        const sessionId = sessionStorage.getItem('aptitude_session_id');
        if (sessionId) {
          const payload = await getAptitudeResult(sessionId);
          setResult(payload);
        }
      }
    };

    preload();
  }, []);

  useEffect(() => {
    if (!result?.totalScore) return undefined;
    const target = Math.round(Number(result.totalScore));
    let current = 0;
    const timer = setInterval(() => {
      current += 1;
      setScore(current);
      if (current >= target) {
        clearInterval(timer);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [result?.totalScore]);

  const sectionRows = useMemo(() => result?.sectionScores || [], [result]);

  const onDownloadReport = () => {
    const popup = window.open('', '_blank');
    if (!popup) return;
    const html = `
      <html>
      <head><title>MindEdge Aptitude Test Report</title></head>
      <body style="font-family: Arial, sans-serif; padding: 24px;">
        <h1>MindEdge Aptitude Test Report</h1>
        <p>Overall Score: ${Math.round(Number(result?.totalScore || 0))}%</p>
        <p>Performance Band: ${result?.band || 'N/A'}</p>
        <table border="1" cellspacing="0" cellpadding="8" style="border-collapse: collapse; margin-top: 12px; width: 100%;">
          <thead><tr><th>Section</th><th>Score</th><th>Time Taken</th><th>Accuracy</th></tr></thead>
          <tbody>
            ${(sectionRows || [])
              .map((row) => `<tr><td>${row.sectionName}</td><td>${row.score}%</td><td>${row.timeTaken}</td><td>${row.accuracy}%</td></tr>`)
              .join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  if (!result) {
    return <div className="min-h-screen aptitude-intro-bg flex items-center justify-center text-white">Loading results...</div>;
  }

  return (
    <div className="min-h-screen aptitude-intro-bg px-4 py-10 text-white">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-sora text-center">MindEdge Aptitude Test</h1>
        <p className="text-center text-white/70 mt-2">Assessment Performance Summary</p>

        <div className="mt-8 aptitude-panel rounded-3xl p-6 md:p-8">
          <div className="text-center">
            <p className="text-white/70">Overall Score</p>
            <p className="text-6xl font-bold mt-2">{score}%</p>
            <p className="mt-2 text-amber-300 font-semibold">{result.band}</p>
          </div>

          <div className="mt-8 grid md:grid-cols-5 gap-4">
            {sectionRows.map((section) => {
              const circle = percentToStroke(section.score);
              return (
                <div key={section.sectionCode} className="rounded-xl border border-white/20 bg-white/10 p-4 text-center">
                  <svg width="110" height="110" viewBox="0 0 120 120" className="mx-auto">
                    <circle cx="60" cy="60" r="46" fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="8" />
                    <circle
                      cx="60"
                      cy="60"
                      r="46"
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={circle.circumference}
                      strokeDashoffset={circle.dashoffset}
                      transform="rotate(-90 60 60)"
                    />
                    <text x="60" y="65" textAnchor="middle" className="fill-white text-sm font-semibold">{section.score}%</text>
                  </svg>
                  <p className="mt-2 text-xs text-white/70">{section.sectionName}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/20 text-white/70">
                  <th className="py-2">Section</th>
                  <th className="py-2">Score</th>
                  <th className="py-2">Time Taken</th>
                  <th className="py-2">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {sectionRows.map((row) => (
                  <tr key={row.sectionCode} className="border-b border-white/10">
                    <td className="py-2">{row.sectionName}</td>
                    <td className="py-2">{row.score}%</td>
                    <td className="py-2">{row.timeTaken}</td>
                    <td className="py-2">{row.accuracy}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={onDownloadReport}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-2.5 font-semibold"
            >
              <Download className="h-4 w-4" />
              Download Report (PDF)
            </button>
            <button
              type="button"
              onClick={() => navigate('/assessment')}
              className="rounded-xl border border-white/25 bg-white/10 px-4 py-2.5"
            >
              Back to Hub
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
